import React, { createContext, useState, useEffect, useContext } from 'react';

// Function to decode JWT token
const decodeToken = (token) => {
  try {
    if (!token) return null;
    
    // JWT tokens have 3 parts separated by dots
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

// Create the context
const UserContext = createContext(null);

// Provider component
export const UserProvider = ({ children }) => {
  const getInitialUserDetails = () => {
    const token = localStorage.getItem('token') || '';
    const email = localStorage.getItem('email') || '';
    
    // Decode token to get type, eventId, and eventName
    const decodedToken = decodeToken(token);
    
    return {
      token,
      email,
      type: decodedToken?.type || '',
      eventId: decodedToken?.eventId || '',
      eventName: decodedToken?.eventName || '',
    };
  };

  const [userDetails, setUserDetails] = useState(getInitialUserDetails);

  // Function to update user details and localStorage
  const updateUserDetails = (newDetails) => {
    const updatedDetails = { ...userDetails, ...newDetails };
    
    // Only update localStorage for token and email
    // type, eventId, and eventName come from token
    if (newDetails.token) {
      localStorage.setItem('token', newDetails.token);
      
      // Decode the new token to get updated type, eventId, eventName
      const decodedToken = decodeToken(newDetails.token);
      updatedDetails.type = decodedToken?.type || '';
      updatedDetails.eventId = decodedToken?.eventId || '';
      updatedDetails.eventName = decodedToken?.eventName || '';
    }
    
    if (newDetails.email) {
      localStorage.setItem('email', newDetails.email);
    }
    
    // Update state
    setUserDetails(updatedDetails);
  };

  // Function to clear user details (for logout)
  const clearUserDetails = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    // Remove the old localStorage items if they exist
    localStorage.removeItem('type');
    localStorage.removeItem('eventId');
    localStorage.removeItem('eventName');
    
    setUserDetails({
      token: '',
      email: '',
      type: '',
      eventId: '',
      eventName: '',
    });
  };

  // Function to refresh user details from token (useful for token updates)
  const refreshFromToken = () => {
    const token = localStorage.getItem('token') || '';
    const email = localStorage.getItem('email') || '';
    const decodedToken = decodeToken(token);
    
    setUserDetails({
      token,
      email,
      type: decodedToken?.type || '',
      eventId: decodedToken?.eventId || '',
      eventName: decodedToken?.eventName || '',
    });
  };

  // Listen for localStorage changes (optional - for multiple tabs)
  useEffect(() => {
    const handleStorageChange = () => {
      refreshFromToken();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Check if token is expired
  const isTokenExpired = () => {
    const decodedToken = decodeToken(userDetails.token);
    if (!decodedToken || !decodedToken.exp) return true;
    
    const currentTime = Date.now() / 1000;
    return decodedToken.exp < currentTime;
  };

  const value = {
    userDetails,
    updateUserDetails,
    clearUserDetails,
    refreshFromToken,
    // Helper functions for specific checks
    isAuthenticated: !!userDetails.token && !isTokenExpired(),
    userEmail: userDetails.email,
    userType: userDetails.type,
    eventId: userDetails.eventId,
    eventName: userDetails.eventName,
    isTokenExpired,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
